/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { Checkbox, DateInput, Form, Grid } from '@mairistem/composants';
import { Section, SectionHeader } from '@mairistem/administres-utils';

import { DATE_FORMAT } from '@/constants';

import { ChampAdresse, STYLE_TITRE_SECTION } from '@/components/Common';

// Module autonome : propre `dayjs.extend`, ne pas dépendre d'un ordre d'import
// fortuit. Sans lui, `dayjs(valeur, DATE_FORMAT)` rend « Invalid Date » —
// silencieusement à l'écran, et de façon reproductible en test isolé.
dayjs.extend(customParseFormat);

interface InformationsContratMariageProps {
  data: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (event: React.SyntheticEvent | null, payload: any) => void;
}

/**
 * Bloc « CONTRAT DE MARIAGE » : existence du contrat, sa date, et le notaire qui
 * l'a reçu.
 *
 * La date et le notaire ne s'affichent QUE si la case est cochée — et l'API
 * purge la date lorsqu'elle est décochée, si bien qu'une date saisie puis
 * masquée ne survit pas en base.
 *
 * ⚠️ Le notaire est un individu du dossier (rôle `NOT`), pas une chaîne :
 * `am_cm_notair` contient chez le client historique une phrase rendue
 * (« Me Albert PORCEL, Notaire à LA SEYNE (Var) ») qui reste vide ici, au profit
 * du nom, du prénom et de l'adresse de l'office.
 */
const InformationsContratMariage = (props: InformationsContratMariageProps) => {
  const { data, errors = {}, onChange } = props;

  const existe = Boolean(data?.contratMariage);

  const handleExisteChange = React.useCallback(
    (
      event: React.SyntheticEvent | null,
      { checked }: { checked?: boolean },
    ) => {
      onChange(event, { name: 'contratMariage', value: Boolean(checked) });
    },
    [onChange],
  );

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      onChange(null, {
        name: 'dateContratMariage',
        value: _.isNil(date) ? null : dayjs(date).format(DATE_FORMAT),
      });
    },
    [onChange],
  );

  /** Validité de la commune de l'office à la date du contrat, sinon aujourd'hui. */
  const dateValiditeOffice = !_.isEmpty(data?.dateContratMariage)
    ? dayjs(data.dateContratMariage, DATE_FORMAT).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');

  return (
    <Section title="CONTRAT DE MARIAGE" height="auto">
      {/* @ts-ignore */}
      <Form style={{ padding: '8px 14px' }}>
        <Grid>
          <Grid.Row style={{ paddingLeft: '1em' }}>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              <Form.Field>
                {/* @ts-ignore */}
                <Checkbox
                  label="Signature d'un contrat"
                  name="contratMariage"
                  checked={existe}
                  onChange={handleExisteChange}
                />
              </Form.Field>
            </Grid.Column>
            {existe && (
              <Grid.Column computer={8} tablet={16} mobile={16}>
                <Form.Field error={!_.isNil(errors?.dateContratMariage)}>
                  <label>Contrat rédigé le</label>
                  <DateInput
                    value={
                      data?.dateContratMariage
                        ? dayjs(data.dateContratMariage, DATE_FORMAT).toDate()
                        : undefined
                    }
                    onChange={handleDateChange}
                  />
                </Form.Field>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
        {existe && (
          <>
            {/* @ts-ignore */}
            <SectionHeader
              title="Notaire"
              required={false}
              style={STYLE_TITRE_SECTION}
            />
            <Grid>
              <Grid.Row style={{ paddingLeft: '1em' }}>
                <Grid.Column computer={8} tablet={8} mobile={16}>
                  <Form.Input
                    fluid
                    label="Nom"
                    name="notaire.nomFamille"
                    maxLength={50}
                    error={!!errors?.nomFamille}
                    value={data?.notaire?.nomFamille ?? ''}
                    onChange={onChange}
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={8} mobile={16}>
                  <Form.Input
                    fluid
                    label="Prénom"
                    name="notaire.prenom"
                    maxLength={100}
                    error={!!errors?.prenom}
                    value={data?.notaire?.prenom ?? ''}
                    onChange={onChange}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <ChampAdresse
              data={data}
              errors={errors}
              onChange={onChange}
              name="notaire.adresseDomicile"
              cleErreur="adresseDomicile"
              title="Office notarial"
              dateValidite={dateValiditeOffice}
            />
          </>
        )}
      </Form>
    </Section>
  );
};

export default InformationsContratMariage;
